import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormControl } from "@angular/forms";
import { firstValueFrom, map, switchMap, take, takeUntil } from "rxjs";
import { IAssigneesStoreState } from "@gtmhub/assignees/redux/assignee-reducer";
import { IIndicator } from "@gtmhub/error-handling";
import { ICheckinComment } from "@gtmhub/goals/models";
import { OKRsEventType } from "@gtmhub/okrs/events";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IReduxTeam } from "@gtmhub/teams/models/models";
import { ITeamsStoreState } from "@gtmhub/teams/redux/teams-reducer";
import { getCurrentUserId } from "@gtmhub/users";
import { IdMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { NewComment } from "@webapp/comments/models/comments.models";
import { CommentsFacade } from "@webapp/comments/services/comments-facade.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { generateRemoveAllMentionsAriaLabel, getOwnerIds } from "@webapp/okrs/utils/metric.utils";
import { FormOf } from "@webapp/shared/form/models/form.models";
import { NoteWithGifAndMentions } from "@webapp/shared/rich-text-editor-with-mentions/rich-text-editor-with-mentions.component";
import { CacheFormOnAccidentalModalClose, CacheableFormGroup } from "@webapp/shared/utils/accidental-modal-close";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";
import { ICheckin, Metric } from "../../models/metric.models";
import { MetricsFacade } from "../../services/metrics-facade.service";

const cacheTimeout = 1000 * 60 * 5; // 5 minutes

export type UpdateMetricNoteModalData = {
  metricId: string;
  checkinId: string;
};

interface UpdateMetricNoteData {
  note: NoteWithGifAndMentions;
}

type UpdateMetricNoteForm = FormOf<UpdateMetricNoteData>;

@Component({
  selector: "update-metric-note",
  templateUrl: "./update-metric-note.component.html",
  styleUrls: ["./update-metric-note.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateMetricNoteComponent implements OnInit {
  private metricId: string;
  private checkinId: string;
  private userId: string;

  public metric: Metric;
  public checkin: ICheckin;
  public assignees: Record<string, Assignee>;
  public teams: IdMap<IReduxTeam>;
  public removeAllMentionsAriaLabel: string;
  public loading = true;
  public indicators: {
    saving?: IIndicator;
  } = {};
  @CacheFormOnAccidentalModalClose({ timeout: cacheTimeout })
  public updateMetricNoteForm: CacheableFormGroup<UpdateMetricNoteForm>;

  constructor(
    private formBuilder: FormBuilder,
    private destroy$: DestroyService,
    private commentsFacade: CommentsFacade,
    private metricsFacade: MetricsFacade,
    private broadcastService: BroadcastService,
    private cdRef: ChangeDetectorRef,
    @Inject(UI_MODAL_DATA) modalData: UpdateMetricNoteModalData
  ) {
    Object.assign(this, modalData);

    this.updateMetricNoteForm = this.formBuilder.group<UpdateMetricNoteForm>({
      note: new FormControl({
        note: null,
        gif: null,
        mentions: {
          removable: [],
          nonRemovable: [],
        },
      }),
    });

    this.updateMetricNoteForm.controls.note.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.removeAllMentionsAriaLabel = generateRemoveAllMentionsAriaLabel(value.mentions, this.assignees);
    });

    this.updateMetricNoteForm.cacheKey = this.checkinId;
  }

  public ngOnInit(): void {
    this.userId = getCurrentUserId();

    const { reduxStore } = reduxStoreContainer;
    const reduxObserver = new ReduxStoreObserver(reduxStore);
    reduxObserver
      .whenFetched$<IAssigneesStoreState & ITeamsStoreState>("assignees", "teams")
      .pipe(takeUntil(this.destroy$))
      .subscribe((storeData) => {
        this.assignees = storeData.assignees.map;
        this.teams = storeData.teams.map;

        this.getMetric(this.metricId);
      });
  }

  public save(): Promise<unknown> {
    const checkinComment: ICheckinComment = {
      comment: this.updateMetricNoteForm.controls.note.value.note,
      gif: this.updateMetricNoteForm.controls.note.value.gif,
    };

    const mentionIds = [...new Set(this.updateMetricNoteForm.controls.note.value.mentions.nonRemovable)];
    const fakeComment: NewComment = {
      mentioned: mentionIds,
      targetId: this.checkin.id,
      targetType: "checkinMention",
      targetTitle: this.metric.name,
      text: checkinComment.comment,
      gif: checkinComment.gif,
    };

    this.indicators.saving = { progress: true };

    const save$ = this.metricsFacade
      .patchMetricComment$(this.checkin.id, checkinComment)
      .pipe(switchMap((newCheckinComment) => this.commentsFacade.postComment$(fakeComment).pipe(map(() => newCheckinComment))));

    return firstValueFrom(save$).then(
      (newCheckinComment) => {
        delete this.indicators.saving;

        this.metricsFacade
          .getMetric$(this.metricId)
          .pipe(take(1))
          .subscribe((metric) => {
            this.broadcastService.emit(OKRsEventType.OKRS_GOALS_CHANGED, {
              reason: "metricCommentEdit",
              detail: {
                metric: metric,
              },
            });
          });
        return newCheckinComment;
      },
      (error) => {
        this.indicators.saving = { error };
        this.cdRef.detectChanges();
      }
    );
  }

  private getMetric(metricId: string): void {
    this.metricsFacade.getMetric$(metricId).subscribe((metric) => {
      this.loading = false;
      this.metric = metric;

      for (const snapshot of this.metric.metricHistory) {
        if (this.checkinId === snapshot.id) {
          this.updateMetricNoteForm.controls.note.setValue({
            ...this.updateMetricNoteForm.controls.note.value,
            note: snapshot.comment,
            gif: snapshot.gif || null,
          });

          this.checkin = snapshot;
          break;
        }
      }

      this.addOwnersToMentioned();

      this.updateMetricNoteForm.loadFromCache();
      this.cdRef.detectChanges();
    });
  }

  private addOwnersToMentioned(): void {
    const allUserOwnerIds = getOwnerIds(this.metric.ownerIds, this.assignees, this.teams);
    allUserOwnerIds.delete(this.userId);

    this.updateMetricNoteForm.controls.note.patchValue({
      ...this.updateMetricNoteForm.controls.note.value,
      mentions: {
        removable: Array.from(allUserOwnerIds),
        nonRemovable: [],
      },
    });
  }
}
